/**
 * Copy of apps/backend/src/lib/analytics-query/time.ts (date logic only) so the
 * date picker can preview ranges. Keep the two in step.
 *
 * Date ranges the way Shopify's date picker defines them, in the store's time
 * zone: presets (Today, Yesterday, Last N days/weeks/months with or without
 * today, period to date, quarters, Black Friday / Cyber Monday) and custom
 * ranges, plus the comparison period (previous period, previous year, previous
 * year matching day of week, custom).
 */
export type Grain = "hour" | "day" | "week" | "month" | "quarter" | "year"
export type RangeSpec = { preset?: string; since?: string; until?: string }
export type CompareSpec = "none" | "previous_period" | "previous_year" | "previous_year_dow" | { since: string; until: string }
export type Resolved = { start: Date; end: Date; since: string; until: string; label: string; days: number; grain: Grain }

const DAY = 86400_000

/** Offset of `tz` from UTC at instant `t`, in ms (local = utc + offset). */
function offset(t: number, tz: string) {
  const p = new Intl.DateTimeFormat("en-US", { timeZone: tz, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" }).formatToParts(new Date(t))
  const g = (k: string) => Number(p.find((x) => x.type === k)!.value)
  return Date.UTC(g("year"), g("month") - 1, g("day"), g("hour") === 24 ? 0 : g("hour"), g("minute"), g("second")) - Math.floor(t / 1000) * 1000
}

/** UTC instant of local midnight (y, m 1-12, d) in `tz`. */
export function localMidnight(y: number, m: number, d: number, tz: string) {
  const guess = Date.UTC(y, m - 1, d)
  let t = guess - offset(guess, tz)
  t = guess - offset(t, tz) // DST edge
  return new Date(t)
}

export type Ymd = { y: number; m: number; d: number }
export function todayLocal(tz: string, now = new Date()): Ymd {
  const [y, m, d] = new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(now).split("-").map(Number)
  return { y, m, d }
}
const fromYmd = (s: string): Ymd => {
  const [y, m, d] = s.split("-").map(Number)
  return { y, m, d }
}
export const ymd = (x: Ymd) => `${x.y}-${String(x.m).padStart(2, "0")}-${String(x.d).padStart(2, "0")}`
/** Calendar arithmetic on dates (no time zone involved). */
export function addDays(x: Ymd, n: number): Ymd {
  const t = new Date(Date.UTC(x.y, x.m - 1, x.d) + n * DAY)
  return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1, d: t.getUTCDate() }
}
function addMonths(x: Ymd, n: number): Ymd {
  const t = new Date(Date.UTC(x.y, x.m - 1 + n, 1))
  const last = new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth() + 1, 0)).getUTCDate()
  return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1, d: Math.min(x.d, last) }
}
const dow = (x: Ymd) => new Date(Date.UTC(x.y, x.m - 1, x.d)).getUTCDay()
const daysBetween = (a: Ymd, b: Ymd) => Math.round((Date.UTC(b.y, b.m - 1, b.d) - Date.UTC(a.y, a.m - 1, a.d)) / DAY)

/** Black Friday = day after US Thanksgiving (4th Thursday of November). */
export function blackFriday(year: number): Ymd {
  const nov1 = dow({ y: year, m: 11, d: 1 })
  const firstThu = 1 + ((4 - nov1 + 7) % 7)
  return { y: year, m: 11, d: firstThu + 21 + 1 }
}

export function grainFor(days: number): Grain {
  if (days <= 1) return "hour"
  if (days <= 92) return "day"
  if (days <= 400) return "week"
  return "month"
}

/** Inclusive local dates [from, to] for a range spec. */
export function rangeDates(spec: RangeSpec, tz: string, now = new Date()): { from: Ymd; to: Ymd; label: string } {
  const today = todayLocal(tz, now)
  const p = spec.preset ?? (spec.since ? "custom" : "last_30_days")
  if (p === "custom" && spec.since && spec.until) {
    let a = fromYmd(spec.since)
    let b = fromYmd(spec.until)
    if (daysBetween(a, b) < 0) [a, b] = [b, a]
    return { from: a, to: b, label: "Custom" }
  }
  if (p === "today") return { from: today, to: today, label: "Today" }
  if (p === "yesterday") {
    const y = addDays(today, -1)
    return { from: y, to: y, label: "Yesterday" }
  }
  const lastN = /^last_(\d+)_(days|weeks|months)(_incl)?$/.exec(p)
  if (lastN) {
    const n = Math.min(Math.max(Number(lastN[1]), 1), 3660)
    const incl = !!lastN[3]
    const to = incl ? today : addDays(today, -1)
    const from = lastN[2] === "days" ? addDays(to, -(n - 1)) : lastN[2] === "weeks" ? addDays(to, -(n * 7 - 1)) : addDays(addMonths(to, -n), 1)
    return { from, to, label: `Last ${n} ${lastN[2].replace(/s$/, n === 1 ? "" : "s")}` }
  }
  if (p === "week_to_date") return { from: addDays(today, -dow(today)), to: today, label: "Week to date" }
  if (p === "month_to_date") return { from: { ...today, d: 1 }, to: today, label: "Month to date" }
  if (p === "quarter_to_date") return { from: { y: today.y, m: Math.floor((today.m - 1) / 3) * 3 + 1, d: 1 }, to: today, label: "Quarter to date" }
  if (p === "year_to_date") return { from: { y: today.y, m: 1, d: 1 }, to: today, label: "Year to date" }
  if (p === "last_week") {
    const end = addDays(today, -dow(today) - 1)
    return { from: addDays(end, -6), to: end, label: "Last week" }
  }
  if (p === "last_month") {
    const first = addMonths({ ...today, d: 1 }, -1)
    return { from: first, to: addDays({ ...today, d: 1 }, -1), label: "Last month" }
  }
  if (p === "last_quarter") {
    const qStart = { y: today.y, m: Math.floor((today.m - 1) / 3) * 3 + 1, d: 1 }
    const from = addMonths(qStart, -3)
    return { from, to: addDays(qStart, -1), label: "Last quarter" }
  }
  if (p === "last_year") return { from: { y: today.y - 1, m: 1, d: 1 }, to: { y: today.y - 1, m: 12, d: 31 }, label: "Last year" }
  const q = /^q([1-4])_(\d{4})$/.exec(p)
  if (q) {
    const m = (Number(q[1]) - 1) * 3 + 1
    const from = { y: Number(q[2]), m, d: 1 }
    return { from, to: addDays(addMonths(from, 3), -1), label: `Q${q[1]} ${q[2]}` }
  }
  const bf = /^(black_friday|cyber_monday|bfcm)_(\d{4})$/.exec(p)
  if (bf) {
    const f = blackFriday(Number(bf[2]))
    if (bf[1] === "black_friday") return { from: f, to: f, label: `Black Friday ${bf[2]}` }
    if (bf[1] === "cyber_monday") return { from: addDays(f, 3), to: addDays(f, 3), label: `Cyber Monday ${bf[2]}` }
    return { from: f, to: addDays(f, 3), label: `BFCM ${bf[2]}` }
  }
  return rangeDates({ preset: "last_30_days" }, tz, now)
}

export function resolveRange(spec: RangeSpec, tz: string, now = new Date()): Resolved {
  const { from, to, label } = rangeDates(spec, tz, now)
  const start = localMidnight(from.y, from.m, from.d, tz)
  const nextDay = addDays(to, 1)
  const end = localMidnight(nextDay.y, nextDay.m, nextDay.d, tz)
  const days = daysBetween(from, to) + 1
  return { start, end, since: ymd(from), until: ymd(to), label, days, grain: grainFor(days) }
}
